import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text} from '@fluentui/react';
import {useExchangeSupportLoginMutation} from '../../store/Api';
import {setSupportAuth} from '../../store/authSlice';
import {setSelectedOrganization} from '../../store/settingsSlice';
import {
    clearSupportSession,
    ISupportSession,
    SUPPORT_SESSION_ENDED,
    SUPPORT_SESSION_REVOKED,
    writeSupportSession
} from '../../logic/supportSession';

/**
 * Landing page for a support login opened from the partner portal. The portal puts a one-time code in the URL
 * fragment; this page takes it out of the address bar before doing anything else (so it is not in history or a
 * bookmark), trades it for a session once, and drops the person on the home page as the support user.
 *
 * Also the "session over" page: ?state=ended (ended here) or ?state=revoked (ended elsewhere, or expired) renders the
 * closing message instead.
 */
const SupportLoginPage: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [exchange] = useExchangeSupportLoginMutation();
    const [error, setError] = useState<string>();
    const started = useRef(false);
    const state = new URLSearchParams(window.location.search).get('state');
    const ended = state === SUPPORT_SESSION_ENDED || state === SUPPORT_SESSION_REVOKED;

    useEffect(() => {
        if (ended || started.current) return;
        started.current = true;

        const code = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('code');
        window.history.replaceState(null, '', window.location.pathname);
        if (!code) {
            setError('This link has no support login code. Start a new session from the partner portal.');
            return;
        }

        exchange({code}).unwrap()
            .then((response) => {
                const session: ISupportSession = {...response};
                writeSupportSession(session);
                dispatch(setSelectedOrganization(session.organization));
                dispatch(setSupportAuth(session));
                history.replace('/');
            })
            .catch(() => {
                clearSupportSession();
                setError('This support login link has expired or has already been used. Start a new session from the partner portal.');
            });
    }, []);

    return (
        <Stack horizontalAlign="center" verticalAlign="center" styles={{root: {minHeight: '100vh', padding: '1em'}}}
               tokens={{childrenGap: '1em'}}>
            <img src="/mainLogo.png" width={250} alt="Adaptive Catalog"/>
            {ended
                ? <Text variant="large">
                    {state === SUPPORT_SESSION_REVOKED
                        ? 'This support session was ended from the partner portal, or it expired. You can close this tab.'
                        : 'Support session ended. You can close this tab.'}
                  </Text>
                : error
                    ? <MessageBar messageBarType={MessageBarType.error} styles={{root: {maxWidth: 480}}}>{error}</MessageBar>
                    : <Spinner size={SpinnerSize.large} label="Starting support session…"/>}
        </Stack>
    );
};

export default SupportLoginPage;
